
/* ============================== src/components/Modals/TaskModal/TaskFormTab.jsx ============================== */
/**
 * Вкладка «Данные» карточки задачи. При isTemplate=true скрываются поля,
 * не имеющие смысла в шаблоне: статус, старт, срок, часовая задача,
 * зависимости. required отключается. Используется в TaskModal и TemplateModal.
 */
import { Ic } from "../../Icons";
import { ICONS } from "../../iconsData";
import { FormField } from "../../FormField";
import { TagsField } from "../../TagsField";
import { TagsList } from "../../TagsList";
import { TemplateSelect } from "../../Templates/TemplateSelect";
import { ToggleSwitch } from "../../ToggleSwitch";
import { ENTITY_FIELDS_META } from "../../../utils/templateSchemas";

const TASK_META = ENTITY_FIELDS_META.task;
const req = (f) => TASK_META[f].requiredInCreate === true;
const lab = (f) => TASK_META[f].label;

export function TaskFormTab({
  form,
  access,
  options,
  template,
  summary,
  onRequestHours,
  onRequestDeadline,
  lockedField,
  approvalHint,
  onOpenProject,
  openTag,
  isTemplate = false,
}) {
  const { values, handleChange, updateValues, touched, errors } = form;
  const { canEditFields, isProjectLocked, isAdminProject, canToggleFlags } =
    access;
  const {
    projectOptions,
    assigneeOptionsList,
    priorityOptions,
    statusOptions,
    dependencyOptions,
    dependencyTypeOptions,
  } = options;
  const { isNew, isCopy, appliedTemplateName, onApply } = template;

  const startLocked = lockedField === "start";
  const deadlineLocked = lockedField === "deadline";

  const projectAfter =
    values.projectId && onOpenProject && !isTemplate ? (
      <button
        type="button"
        className="btn ghost sm"
        onClick={() => onOpenProject(values.projectId)}
        title="Перейти к проекту"
      >
        Перейти
      </button>
    ) : null;

  const hasTags = Array.isArray(values.tags) && values.tags.length > 0;

  return (
    <div className="project-info-fields">
      {!isTemplate && isNew && !isCopy && (
        <TemplateSelect kind="task" onApply={onApply} />
      )}

      {!isTemplate && isNew && appliedTemplateName && (
        <div className="info-box">
          Применён шаблон: <b>{appliedTemplateName}</b>
        </div>
      )}

      <FormField
        label={lab("title")}
        required={req("title") && !isTemplate}
        value={values.title ?? ""}
        onChange={(v) => handleChange("title", v)}
        error={touched.title && errors.title}
        disabled={!canEditFields}
        inline
      />

      {!isTemplate && (
        <div className="field-row">
          <label className="field-label" />
          <div className="flex-1 flex gap-6 items-center">
            <ToggleSwitch
              checked={summary.checked}
              onChange={(v) => handleChange("isSummary", v)}
              label="Суммарная задача"
              disabled={summary.disabled || !canToggleFlags}
            />
            <ToggleSwitch
              checked={values.isHourly}
              onChange={(v) => updateValues({ isHourly: v })}
              label={lab("isHourly")}
              disabled={!canEditFields || !canToggleFlags}
            />
          </div>
        </div>
      )}

      <div className="fields-row fields-row--stacked">
        <div className="field-row">
          <label className="field-label">{lab("desc")}</label>
          <div className="flex-1">
            <textarea
              className="inp"
              rows={2}
              value={values.desc ?? ""}
              onChange={(e) => handleChange("desc", e.target.value)}
              disabled={!canEditFields}
            />
          </div>
        </div>
      </div>

      {canEditFields ? (
        <TagsField
          value={values.tagsInput ?? ""}
          onChange={(v) => handleChange("tagsInput", v)}
          error={touched.tagsInput && errors.tagsInput}
        />
      ) : (
        hasTags && (
          <div className="field-row">
            <label className="field-label">{lab("tags")}</label>
            <div className="flex-1">
              <TagsList
                tags={values.tags}
                onTagClick={openTag}
                interactive={false}
              />
            </div>
          </div>
        )
      )}

      <FormField
        label={lab("projectId")}
        required={req("projectId") && !isTemplate}
        type="select"
        options={projectOptions}
        value={values.projectId ?? ""}
        onChange={(v) => handleChange("projectId", v)}
        error={touched.projectId && errors.projectId}
        disabled={!canEditFields || isProjectLocked}
        after={projectAfter}
        inline
      />

      <div className="fields-with-action">
        <div className="fields-row">
          <FormField
            label={lab("assigneeId")}
            required={req("assigneeId") && !isTemplate}
            type="select"
            options={assigneeOptionsList}
            value={values.assigneeId ?? ""}
            onChange={(v) => handleChange("assigneeId", v)}
            error={touched.assigneeId && errors.assigneeId}
            disabled={!canEditFields}
            inline
          />
          <FormField
            label={lab("plannedHours")}
            required={req("plannedHours") && !isAdminProject && !isTemplate}
            type="number"
            min="0.5"
            step="0.5"
            value={values.plannedHours ?? ""}
            onChange={(v) => handleChange("plannedHours", v)}
            error={touched.plannedHours && errors.plannedHours}
            disabled={!canEditFields || values.isHourly}
            inline
          />
        </div>
        {!isTemplate && onRequestHours && (
          <div className="field-action-row">
            <button
              type="button"
              className="btn request-hours field-action"
              onClick={onRequestHours}
            >
              <Ic d={ICONS.clock} size={14} /> Запросить изменение часов
            </button>
          </div>
        )}
      </div>

      {!isTemplate && approvalHint && (
        <div className="info-box">{approvalHint}</div>
      )}

      <div className="fields-row">
        <FormField
          label={lab("priority")}
          required={req("priority") && !isTemplate}
          type="select"
          options={priorityOptions}
          value={values.priority ?? ""}
          onChange={(v) => handleChange("priority", v)}
          error={touched.priority && errors.priority}
          disabled={!canEditFields}
          inline
        />
        {!isTemplate && (
          <FormField
            label="Статус"
            required
            type="select"
            options={statusOptions}
            value={values.status ?? ""}
            onChange={(v) => handleChange("status", v)}
            error={touched.status && errors.status}
            disabled={statusOptions.length <= 1}
            inline
          />
        )}
      </div>

      {!isTemplate && (
        <div className="fields-with-action">
          <div className="fields-row">
            <FormField
              label={lab("start")}
              required={req("start")}
              type="date"
              value={values.start ?? ""}
              onChange={(v) => updateValues({ start: v })}
              error={touched.start && errors.start}
              disabled={!canEditFields || startLocked}
              inline
            />
            <FormField
              label={lab("deadline")}
              required={req("deadline") && !isAdminProject}
              type="date"
              value={values.deadline ?? ""}
              onChange={(v) => handleChange("deadline", v)}
              error={touched.deadline && errors.deadline}
              disabled={!canEditFields || values.isHourly || deadlineLocked}
              inline
            />
          </div>
          {onRequestDeadline && (
            <div className="field-action-row">
              <button
                type="button"
                className="btn request-hours field-action"
                onClick={onRequestDeadline}
              >
                <Ic d={ICONS.cal} size={14} /> Запросить изменение срока
              </button>
            </div>
          )}
        </div>
      )}

      {!isTemplate && values.isHourly && (
        <div className="fields-row">
          <FormField
            label={lab("startTime")}
            required={req("startTime")}
            type="time"
            value={values.startTime ?? ""}
            onChange={(v) => updateValues({ startTime: v })}
            error={touched.startTime && errors.startTime}
            disabled={!canEditFields}
            inline
          />
          <FormField
            label={lab("endTime")}
            required={req("endTime")}
            type="time"
            value={values.endTime ?? ""}
            onChange={(v) => updateValues({ endTime: v })}
            error={touched.endTime && errors.endTime}
            disabled={!canEditFields}
            inline
          />
        </div>
      )}

      {!isTemplate && (
        <>
          <FormField
            label={lab("dependencyId")}
            type="select"
            options={dependencyOptions}
            value={values.dependencyId ?? ""}
            onChange={(v) => handleChange("dependencyId", v)}
            disabled={!canEditFields}
            inline
          />
          <FormField
            label={lab("dependencyType")}
            type="select"
            options={dependencyTypeOptions}
            value={values.dependencyType ?? ""}
            onChange={(v) => handleChange("dependencyType", v)}
            disabled={!canEditFields || !values.dependencyId}
            inline
          />
        </>
      )}
    </div>
  );
}
